import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Alert,
} from '@mui/material';
import api from '../../api/axiosConfig';
import { useInstatDesign, heatmapColor, withAlpha } from './theme/instatDesign';
import { Card, Pill, Skeleton, Sparkline } from './ui';
import { openCareerStatVideo } from './instatVideo';

const COL_GROUPS = [
  { keys: ['GP', 'MIN', 'PTS'], label: '' },
  { keys: ['FGM', 'FGA', 'FG%'], label: 'Field goals' },
  { keys: ['3PM', '3PA', '3P%'], label: '3-point' },
  { keys: ['FTM', 'FTA', 'FT%'], label: 'Free throws' },
  { keys: ['OR', 'DR', 'REB'], label: 'Rebounds' },
  { keys: ['AST', 'STL', 'BLK', 'TO', 'PF'], label: '' },
];

// Percentage columns get a blue→green heatmap background.
// Non-% columns: only PTS/REB/AST get a faint accent bar for "best of career".
const PCT_KEYS = new Set(['FG%', '3P%', 'FT%']);

// Rough percentage ranges where the heatmap should look good.
// FG% typical range 35-60 ; 3P% 25-45 ; FT% 60-90
const PCT_RANGE = {
  'FG%': [35, 65],
  '3P%': [20, 50],
  'FT%': [55, 95],
};

function pctNormalized(key, val) {
  const range = PCT_RANGE[key];
  if (!range) return 0.5;
  const [lo, hi] = range;
  return Math.max(0, Math.min(1, (Number(val) - lo) / (hi - lo)));
}

function Cell({ value, valueKey, isMax, clickable, onClick }) {
  const d = useInstatDesign();
  if (value == null || value === '') {
    return (
      <TableCell align="right" sx={{ color: d.colors.textTertiary, ...d.typo.tabular }}>—</TableCell>
    );
  }
  const pct = PCT_KEYS.has(valueKey);
  const bg = pct ? heatmapColor(pctNormalized(valueKey, value)) : 'transparent';

  return (
    <TableCell
      align="right"
      onClick={clickable ? onClick : undefined}
      sx={{
        position: 'relative',
        fontFamily: d.typo.fontFamily,
        color: d.colors.text,
        fontWeight: isMax ? 700 : 500,
        cursor: clickable ? 'pointer' : 'default',
        ...d.typo.tabular,
        transition: `background-color ${d.motion.fast}`,
        '&::before': pct ? {
          content: '""',
          position: 'absolute',
          top: 4,
          bottom: 4,
          left: 4,
          right: 4,
          borderRadius: `${d.radius.sm}px`,
          bgcolor: bg,
          opacity: 0.55,
          zIndex: 0,
        } : {},
        '&:hover': clickable ? {
          bgcolor: d.accentSoft,
          '& .career-cell-play': { opacity: 1 },
        } : {},
      }}
    >
      <Box component="span" sx={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
        {pct ? `${value}%` : value}
        {clickable && (
          <Box
            className="career-cell-play"
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: d.accent,
              opacity: 0.35,
              transition: `opacity ${d.motion.fast}`,
            }}
          />
        )}
      </Box>
    </TableCell>
  );
}

export default function CareerTab({ playerId, playerName, playerContext = null }) {
  const d = useInstatDesign();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!playerId) return;
    let aborted = false;
    setLoading(true);
    setError(null);
    setData(null);
    api
      .get(`/api/instat/player/${playerId}/career`)
      .then(({ data }) => { if (!aborted) setData(data); })
      .catch((e) => {
        if (!aborted) {
          const detail = e.response?.data?.error || e.response?.data || e.message;
          setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
        }
      })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [playerId]);

  // For each numeric column, find the "best" season value (max) so we can
  // bold it in the table as a mini career-high highlight.
  const maxByCol = useMemo(() => {
    if (!data?.seasons?.length) return {};
    const out = {};
    for (const g of COL_GROUPS) {
      for (const key of g.keys) {
        let best = -Infinity;
        for (const s of data.seasons) {
          const v = s.stats?.[key];
          if (typeof v === 'number' && v > best) best = v;
        }
        if (Number.isFinite(best)) out[key] = best;
      }
    }
    return out;
  }, [data]);

  // Chronological PTS trend for the sparkline column (career arc)
  const ptsSeriesChronological = useMemo(() => {
    if (!data?.seasons?.length) return [];
    return [...data.seasons].reverse().map((s) => ({ value: s.stats?.PTS, label: s.name }));
  }, [data]);

  if (loading) {
    return (
      <Card>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Skeleton height={24} width={160} />
          <Skeleton height={20} width={80} variant="pill" />
        </Stack>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={28} sx={{ mb: 1 }} />
        ))}
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <Alert severity="error" sx={{ whiteSpace: 'pre-wrap', borderRadius: `${d.radius.md}px` }}>{error}</Alert>
      </Card>
    );
  }
  if (!data?.seasons?.length) {
    return (
      <Card>
        <Typography sx={{ ...d.typo.caption }}>No career data.</Typography>
      </Card>
    );
  }

  const flatColKeys = COL_GROUPS.flatMap((g) => g.keys);

  return (
    <Card padding={0}>
      <Box sx={{ p: { xs: 2, md: 3 }, pb: 2, display: 'flex', alignItems: { sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography sx={{ ...d.typo.title, color: d.colors.text }}>Career</Typography>
          <Typography sx={{ ...d.typo.caption }}>
            {data.seasons.length} seasons · per-game averages
          </Typography>
        </Box>
        <Pill tone="neutral" size="sm">
          {data.player?.name_eng || `Player ${playerId}`}
        </Pill>
      </Box>

      <Box sx={{ overflowX: 'auto', borderTop: `1px solid ${d.colors.divider}` }}>
        <Table
          size="small"
          sx={{
            minWidth: 1300,
            '& .MuiTableCell-root': {
              fontFamily: d.typo.fontFamily,
              fontSize: 13,
              borderBottom: `1px solid ${d.colors.divider}`,
              py: 0.9,
              whiteSpace: 'nowrap',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{
                  ...d.typo.micro,
                  color: d.colors.textSecondary,
                  bgcolor: d.colors.canvasAlt,
                  borderBottom: `1px solid ${d.colors.divider}`,
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  minWidth: 100,
                }}
              >
                Season
              </TableCell>
              <TableCell rowSpan={2} sx={{ ...d.typo.micro, color: d.colors.textSecondary, bgcolor: d.colors.canvasAlt, minWidth: 150 }}>
                Team
              </TableCell>
              <TableCell rowSpan={2} sx={{ ...d.typo.micro, color: d.colors.textSecondary, bgcolor: d.colors.canvasAlt, minWidth: 180 }}>
                Competition
              </TableCell>
              {COL_GROUPS.map((g, i) => (
                <TableCell
                  key={i}
                  align="center"
                  colSpan={g.keys.length}
                  sx={{
                    ...d.typo.micro,
                    color: d.colors.textSecondary,
                    bgcolor: g.label ? withAlpha('#007AFF', 0.04) : d.colors.canvasAlt,
                    borderBottom: `1px solid ${d.colors.divider}`,
                  }}
                >
                  {g.label}
                </TableCell>
              ))}
              <TableCell rowSpan={2} sx={{ ...d.typo.micro, color: d.colors.textSecondary, bgcolor: d.colors.canvasAlt, minWidth: 120 }}>
                Trend (PTS)
              </TableCell>
            </TableRow>
            <TableRow>
              {flatColKeys.map((k) => (
                <TableCell
                  key={k}
                  align="right"
                  sx={{
                    ...d.typo.micro,
                    color: d.colors.textSecondary,
                    bgcolor: d.colors.canvasAlt,
                    borderBottom: `1px solid ${d.colors.divider}`,
                  }}
                >
                  {k}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.seasons.map((s, idx) => {
              // Mini sparkline for this row: the full career arc so far, with
              // this season's value bolded — for visual consistency we show the
              // same series on every row (career trajectory). One sparkline
              // compressed into the row, with the *current row's* dot as highlight.
              return (
                <TableRow
                  key={s.id}
                  sx={{
                    transition: `background-color ${d.motion.fast}`,
                    '&:hover': { bgcolor: d.colors.canvasAlt },
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, color: d.colors.text, position: 'sticky', left: 0, bgcolor: d.colors.surface, zIndex: 1 }}>
                    {s.name}
                  </TableCell>
                  <TableCell sx={{ color: d.colors.text, fontWeight: 500 }}>{s.team || '—'}</TableCell>
                  <TableCell sx={{ color: d.colors.textSecondary, maxWidth: 220 }}>
                    <Tooltip title={(s.tournaments || []).join(' · ')} arrow>
                      <Typography variant="caption" noWrap sx={{ display: 'block', fontSize: 11.5 }}>
                        {(s.tournaments || []).slice(0, 2).join(', ')}
                        {(s.tournaments?.length || 0) > 2 ? ` +${s.tournaments.length - 2}` : ''}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  {flatColKeys.map((k) => {
                    const v = s.stats?.[k];
                    const meta = s.statsMeta?.[k];
                    const isMax = typeof v === 'number' && v === maxByCol[k] && v > 0;
                    const clickable = Boolean(meta?.clickable && meta?.statparam && s.id);
                    const handleClick = () =>
                      openCareerStatVideo({
                        playerId,
                        playerName,
                        seasonId: s.id,
                        seasonLabel: s.name,
                        stat: { statparam: meta.statparam, label: k },
                        playerContext,
                      });
                    return (
                      <Cell
                        key={k}
                        value={v}
                        valueKey={k}
                        isMax={isMax}
                        clickable={clickable}
                        onClick={handleClick}
                      />
                    );
                  })}
                  <TableCell sx={{ p: 0.5 }}>
                    <Sparkline
                      data={ptsSeriesChronological}
                      width={110}
                      height={30}
                      color={d.accent}
                      showDots={idx === 0}
                      strokeWidth={1.5}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderTop: `1px solid ${d.colors.divider}` }}>
        <Typography sx={{ ...d.typo.caption }}>
          Per-game averages, except <b>GP</b> (total). FG%, 3P%, FT% shaded by performance (cold → hot).
          Cells marked with a dot are clickable — opens the Hudl playlist for that season.
        </Typography>
      </Box>
    </Card>
  );
}
